import React from 'react';
import { CommonDropdownOutlined, ModalFactory } from '@Components';
import { ReceivingMethodFormValueType } from './types/types';
import { CORPORATE, INDIVIDUAL } from '@Utils/constants';
import { buildApiUrl, getListOfCountries, transformDropDownValues } from './methods';
import { API_URL } from '@Utils/config';
import { AppDispatch } from '../../../../../store.type';
import { change, destroy, untouch } from 'redux-form';
import { resetFields } from '@Utils/library';
import { required } from '@Utils/validation';
import PopularCountriesDropdownModal from '../components/popular-countries-dropdown/PopularCountriesDropdown';
import { CASH_ICON, PIGGY_BANK_ICON, MOBILE_WALLET_ICON, CARD_ICON } from '@Images';
import { RECEIVING_METHOD_FORM_NAME } from '../../../constants';
import { ReceivingMethodBankAccountCountryObjectType } from '../../../types/redux-types';
import { resetFormValueStateAction } from '@Redux/actions/new-bene-flow/new-bene-flow-action';
import {
  BENE_BANK_DETAILS_FORM_NAME,
  BENE_PERSONAL_DETAILS_FORM_NAME
} from '../../../constants/commons';

export const options = [
  { key: INDIVIDUAL, value: INDIVIDUAL, text: 'Individual' },
  { key: CORPORATE, value: CORPORATE, text: 'Corporate' }
];

export const HEADING = 'Recipient receiving methods';
export const payoutMethodInfo: Record<string, { image: string }> = {
  MONEYGRAM_CASH_PAYOUT: {
    image: CASH_ICON
  },
  CASH_PICKUP: {
    image: CASH_ICON
  },
  BANK: {
    image: PIGGY_BANK_ICON
  },
  E_WALLET: {
    image: MOBILE_WALLET_ICON
  },
  VISA_DEBIT_CARD: {
    image: CARD_ICON
  },
  PROXY: {
    image: MOBILE_WALLET_ICON
  },
  CARD: {
    image: CARD_ICON
  },
  WALLET: {
    image: MOBILE_WALLET_ICON
  }
};

export const valueToRoutingTypeMap: Record<string, string | string[]> = {
  card: 'VISA_DEBIT_CARD',
  bank: 'BANK',
  wallet: ['E_WALLET', 'WALLET'],
  cash: 'CASH_PICKUP'
};

export const receivingMethodFields = (
  formValues: ReceivingMethodFormValueType,
  dispatch: AppDispatch,
  dropdownRef: React.MutableRefObject<HTMLInputElement | undefined>,
  dropdownValues: ReceivingMethodBankAccountCountryObjectType[],
  popularCountries: ReceivingMethodBankAccountCountryObjectType[],
  disableField = false,
  userAccountCountry?: string
) => {
  const { account_type = '', bank_account_country = '', currency_code = '' } = formValues || {};
  const fields = [
    {
      name: 'account_type',
      component: CommonDropdownOutlined,
      staticValues: options,
      options,
      width: '100%',
      type: 'dropdown',
      bindToThisObject: account_type,
      placeholder: 'Type',
      required: true,
      validate: [required],
      disabled: disableField,
      onDropdownChange: async (object: ReceivingMethodBankAccountCountryObjectType) => {
        resetFields(
          RECEIVING_METHOD_FORM_NAME,
          { bank_account_country: null, currency_code: null, payout_method: null },
          dispatch,
          change,
          untouch,
          false
        );
        dispatch(resetFormValueStateAction());
        dispatch(destroy(BENE_PERSONAL_DETAILS_FORM_NAME));
        dispatch(destroy(BENE_BANK_DETAILS_FORM_NAME));
        //@ts-ignore
        await getListOfCountries(object.value, dispatch, popularCountries, userAccountCountry);
      }
    },

    {
      name: 'bank_account_country',
      component: CommonDropdownOutlined,
      width: '100%',
      type: 'dropdown',
      bindToThisObject: bank_account_country,
      placeholder: 'Recipient receiving country',
      dependedOnObject: account_type ? null : false,
      required: true,
      disabled: !account_type || dropdownValues.length === 0 || disableField,
      parentClassName: 'bank_account_country__dropdown',
      options: transformDropDownValues(dropdownValues),
      staticValues: transformDropDownValues(dropdownValues),

      onClick: async () => {
        //@ts-ignore
        await ModalFactory.builder(
          (onClose: any) => (
            <PopularCountriesDropdownModal
              dispatch={dispatch}
              onClose={onClose}
              account_type={account_type}
              bank_account_country={bank_account_country}
              dropdownRef={dropdownRef}
            />
          ),
          'insta-modal center-modal',
          true
        );

        dropdownRef.current?.focus();
      },

      validate: [required]
    },
    {
      name: 'currency_code',
      component: CommonDropdownOutlined,
      width: '100%',
      type: 'dropdown',
      bindToThisObject: currency_code,
      dependedOnObject: bank_account_country || false,
      url: buildApiUrl(`${API_URL}/beneficiary/currencies/dropdown`, {
        account_type,
        country_code: bank_account_country
      }),
      placeholder: 'Currency',
      preselect_if_one_item: true,
      required: true,
      validate: [required],
      disabled: !account_type || !bank_account_country || disableField
    }
  ];
  return fields;
};

export const POPULAR_COUNTRIES_CONSTANTS = {
  heading: 'Select receiving country'
};

const popularCountriesHeader = [
  {
    key: '',
    text: '',
    disabled: true,
    type: 'heading',
    className: 'menu__country-dropdown-heading-empty',
    header: true
  },
  {
    key: 'popular-countries',
    text: 'Popular countries',
    disabled: true,
    type: 'heading',
    className: 'menu__country-dropdown-heading',
    header: true
  }
];

const allCountriesHeader = {
  key: 'all-countries',
  text: 'All countries',
  type: 'heading',
  className: 'menu__country-dropdown-heading',
  header: true
};

// This is to prevent repetition of the item common for multiple countries in the dropdown list.
const popularCountryEntries = {
  IN: { key: 'IN', value: 'IN', text: 'India', phone_code: ['91'] },
  PH: { key: 'PH', value: 'PH', text: 'Philippines', phone_code: ['63'] },
  NP: { key: 'NP', value: 'NP', text: 'Nepal', phone_code: ['977'] },
  US: { key: 'US', value: 'US', text: 'United States of America', phone_code: ['1'] },
  VN: { key: 'VN', value: 'VN', text: 'Vietnam', phone_code: ['84'] },
  MY: { key: 'MY', value: 'MY', text: 'Malaysia', phone_code: ['60'] },
  SG: { key: 'SG', value: 'SG', text: 'Singapore', phone_code: ['65'] },
  TH: { key: 'TH', value: 'TH', text: 'Thailand', phone_code: ['66'] },
  ID: { key: 'ID', value: 'ID', text: 'Indonesia', phone_code: ['62'] },
  CA: { key: 'CA', value: 'CA', text: 'Canada', phone_code: ['1'] }
};

const buildPopularCountries = (...countryCodes: (keyof typeof popularCountryEntries)[]) => [
  ...popularCountriesHeader,
  ...countryCodes.map((code) => popularCountryEntries[code]),
  allCountriesHeader
];

export const popularCountries = {
  CORPORATE: {
    DEFAULT: buildPopularCountries('IN', 'MY', 'NP'),
    AU: buildPopularCountries('IN', 'PH', 'NP'),
    CA: buildPopularCountries('IN', 'US', 'PH'),
    EU: buildPopularCountries('IN', 'NP', 'VN'),
    HK: buildPopularCountries('IN', 'MY', 'SG'),
    MY: buildPopularCountries('IN', 'TH', 'ID'),
    SG: buildPopularCountries('IN', 'MY', 'NP'),
    GB: buildPopularCountries('IN', 'VN', 'US'),
    US: buildPopularCountries('IN', 'NP', 'CA')
  },
  INDIVIDUAL: {
    DEFAULT: buildPopularCountries('IN', 'MY', 'NP'),
    AU: buildPopularCountries('IN', 'PH', 'NP'),
    CA: buildPopularCountries('IN', 'US', 'PH'),
    EU: buildPopularCountries('IN', 'NP', 'VN'),
    HK: buildPopularCountries('IN', 'MY', 'SG'),
    MY: buildPopularCountries('IN', 'TH', 'ID'),
    SG: buildPopularCountries('IN', 'MY', 'NP'),
    GB: buildPopularCountries('IN', 'VN', 'US'),
    US: buildPopularCountries('IN', 'NP', 'CA')
  }
};
